import { Close, Person, Edit } from "@mui/icons-material";
import { Alert, Snackbar, TextField, Button, Avatar, CircularProgress } from "@mui/material";
import Box from '@mui/material/Box';
import IconButton from "@mui/material/IconButton";
import Modal from '@mui/material/Modal';
import * as React from 'react';
import { useContext, useEffect, useState } from "react";
import { CommonContext } from "../../../contexts/CommonContext";
import useAxios from "../../../hooks/useAxios";
import { User } from '../../../Models/User';
import {
    EMAIL_REG_EXPRESSION,
    UPDATE_USER_URL,
    NAME_REG_EXPRESSION,
    NAME_MIN_LENGTH,
    NAME_MAX_LENGTH
} from "../../../utils/constant";
import styled from "styled-components";
import { logger } from '../../../utils/logger';


// Styled Components harmonieux avec le design existant
const StyledModal = styled(Modal)`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled(Box)`
  background: rgba(255, 255, 255, 0.95) !important;
  backdrop-filter: blur(20px);
  border-radius: 20px !important;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15) !important;
  width: 500px;
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;
  animation: modalSlideIn 0.3s ease-out;
  
  @keyframes modalSlideIn {
    from {
      opacity: 0;
      transform: scale(0.9) translateY(-20px);
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
  color: white;
  border-radius: 20px 20px 0 0;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
`;

const UserAvatarLarge = styled(Avatar)`
  && {
    width: 50px;
    height: 50px;
    background: rgba(255, 255, 255, 0.2);
    border: 2px solid rgba(255, 255, 255, 0.3);
  }
`;

const ModalTitle = styled.div`
  h3 {
    margin: 0;
    font-size: 1.3rem;
    font-weight: 600;
    color: white;
  }
  
  p {
    margin: 0.25rem 0 0 0;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.8);
  }
`;

const CloseButton = styled(IconButton)`
  && {
    color: white;
    background: rgba(255, 255, 255, 0.1);
    border: 1px solid rgba(255, 255, 255, 0.2);
    
    &:hover {
      background: rgba(255, 255, 255, 0.2);
      transform: rotate(90deg);
    }
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const StyledTextField = styled(TextField)`
  && {
    margin-bottom: 1rem;
    
    .MuiOutlinedInput-root {
      background: rgba(255, 255, 255, 0.9);
      border-radius: 12px;
      transition: all 0.3s ease;
      
      &:hover {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.1);
      }
      
      &.Mui-focused {
        background: rgba(255, 255, 255, 1);
        transform: translateY(-2px);
        box-shadow: 0 8px 20px rgba(0, 0, 0, 0.15);
      }
      
      & .MuiOutlinedInput-notchedOutline {
        border-color: rgba(102, 126, 234, 0.2);
        border-width: 2px;
      }
      
      &:hover .MuiOutlinedInput-notchedOutline {
        border-color: rgba(102, 126, 234, 0.4);
      }
      
      &.Mui-focused .MuiOutlinedInput-notchedOutline {
        border-color: var(--primary-color);
      }
    }
    
    .MuiInputLabel-root {
      font-weight: 600;
      color: var(--text-secondary);
      
      &.Mui-focused {
        color: var(--primary-color);
      }
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
`;

const CancelButton = styled(Button)`
  && {
    background: rgba(102, 126, 234, 0.1);
    color: var(--primary-color);
    border: 2px solid rgba(102, 126, 234, 0.2);
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    text-transform: none;
    
    &:hover {
      background: rgba(102, 126, 234, 0.15);
      border-color: rgba(102, 126, 234, 0.3);
      transform: translateY(-2px);
    }
  }
`;

const SubmitButton = styled(Button)`
  && {
    background: linear-gradient(45deg, var(--primary-color), var(--secondary-color));
    color: white;
    border-radius: 12px;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    text-transform: none;
    box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 8px 25px rgba(102, 126, 234, 0.4);
    }
    
    &:disabled {
      background: rgba(0, 0, 0, 0.12);
      color: rgba(0, 0, 0, 0.26);
      transform: none;
      box-shadow: none;
    }
  }
`;

const AlertContainer = styled.div`
  margin-bottom: 1rem;
  
  .MuiAlert-root {
    border-radius: 12px;
    backdrop-filter: blur(10px);
  }
`;

export default function AdminUserEditingModal(props: { openModal: boolean, setOpenModal: Function, openedUser: User, setOpenedUser: Function, onUserUpdate: Function }) {

    const onClose = () => props.setOpenModal(false);

    const handleClose = (event: React.MouseEvent, reason: 'backdropClick' | 'escapeKeyDown') => {
        if (reason !== 'backdropClick') {
            onClose();
        }
    };

    const { setNeedToRefreshData } = useContext(CommonContext);
    const { putWithAxios } = useAxios();

    const [globalError, setGlobalError] = useState('');
    const [globalInfo, setGlobalInfo] = useState('');
    const [validationErrors, setValidationError] = useState({ email: '', firstname: '', lastname: '' });
    const [isLoading, setIsLoading] = useState(false);
    const [originalUser, setOriginalUser] = useState<{email: string, firstname: string, lastname: string} | null>(null);

    // Stocker les valeurs initiales UNIQUEMENT à l'ouverture du modal
    useEffect(() => {
        if (props.openModal && props.openedUser) {
            // Ne stocker que si le modal vient de s'ouvrir (originalUser est null)
            if (!originalUser) {
                setOriginalUser({
                    email: props.openedUser.email,
                    firstname: props.openedUser.firstname,
                    lastname: props.openedUser.lastname
                });
            }
        } else {
            // Réinitialiser quand le modal se ferme
            setOriginalUser(null);
        }
    }, [props.openModal]); // ⚠️ NE PAS mettre props.openedUser ici!

    /**
     * Validation complète alignée avec backend
     */
    const validateField = (field: 'email' | 'firstname' | 'lastname', value: string): string => {
        if (field === 'email') {
            if (value.trim() === '') return "L'email est requis";
            if (!EMAIL_REG_EXPRESSION.test(value)) return "Format d'email invalide";
            return '';
        }

        // Validation firstname/lastname
        if (value.trim() === '') return "Ce champ est requis";
        if (value.length < NAME_MIN_LENGTH) return `Minimum ${NAME_MIN_LENGTH} caractères requis`;
        if (value.length > NAME_MAX_LENGTH) return `Maximum ${NAME_MAX_LENGTH} caractères autorisés`;
        if (!NAME_REG_EXPRESSION.test(value)) return "Lettres, espaces, trait d'union et apostrophes uniquement";
        return '';
    };

    useEffect(() => {
        setValidationError({
            firstname: validateField('firstname', props.openedUser.firstname),
            lastname: validateField('lastname', props.openedUser.lastname),
            email: validateField('email', props.openedUser.email)
        });
    }, [props.openedUser]);

    const updateUser = async (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        setIsLoading(true);
        setGlobalError('');

        // Construction du payload avec uniquement les champs modifiés
        const payload: {email?: string, firstname?: string, lastname?: string} = {};

        if (originalUser) {
            if (props.openedUser.email !== originalUser.email) {
                payload.email = props.openedUser.email;
            }
            if (props.openedUser.firstname !== originalUser.firstname) {
                payload.firstname = props.openedUser.firstname;
            }
            if (props.openedUser.lastname !== originalUser.lastname) {
                payload.lastname = props.openedUser.lastname;
            }
        }

        // Vérifier qu'au moins un champ a été modifié
        if (Object.keys(payload).length === 0) {
            setGlobalError("Aucune modification détectée");
            setIsLoading(false);
            return;
        }

        try {
            await putWithAxios(UPDATE_USER_URL(props.openedUser.uuid!!), payload);
            setGlobalError("");
            setGlobalInfo("Utilisateur mis à jour avec succès");
            setNeedToRefreshData(true);
        } catch (err: any) {
            logger.error('Error updating user:', err);

            // Gestion d'erreurs spécifiques selon backend
            if (err.message?.includes('Email already exists') || err.message?.includes('email')) {
                setGlobalError("Cet email est déjà utilisé par un autre utilisateur");
            } else if (err.message?.includes('User not found')) {
                setGlobalError("Utilisateur introuvable");
            } else if (err.message?.includes('Access denied') || err.message?.includes('Forbidden')) {
                setGlobalError("Accès refusé : droits administrateur requis");
            } else {
                setGlobalError(err.message || "Erreur lors de la mise à jour");
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <StyledModal
            open={props.openModal}
            onClose={handleClose}
            aria-labelledby="modal-mod-title"
            aria-describedby="modal-mod-description"
        >
            <ModalContent>
                <ModalHeader>
                    <HeaderLeft>
                        <UserAvatarLarge>
                            <Person />
                        </UserAvatarLarge>
                        <ModalTitle>
                            <h3>Modifier l'utilisateur</h3>
                            <p>{props.openedUser.firstname} {props.openedUser.lastname}</p>
                        </ModalTitle>
                    </HeaderLeft>
                    <CloseButton onClick={onClose}>
                        <Close />
                    </CloseButton>
                </ModalHeader>

                <ModalBody>
                    {globalError !== '' && (
                        <AlertContainer>
                            <Alert severity="error">{globalError}</Alert>
                        </AlertContainer>
                    )}

                    <Snackbar
                        open={globalInfo !== ''}
                        autoHideDuration={2000}
                        onClose={() =>{
                            setGlobalInfo("");
                            props.setOpenModal(false);
                        }}
                        message={globalInfo}
                    />

                    <form>
                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Nom de famille"
                            error={validationErrors.lastname !== ''}
                            helperText={validationErrors.lastname}
                            value={props.openedUser.lastname}
                            onChange={(e) => props.setOpenedUser({ ...props.openedUser, lastname: e.target.value })}
                            disabled={isLoading}
                        />

                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Prénom(s)"
                            error={validationErrors.firstname !== ''}
                            helperText={validationErrors.firstname}
                            value={props.openedUser.firstname}
                            onChange={(e) => props.setOpenedUser({ ...props.openedUser, firstname: e.target.value })}
                            disabled={isLoading}
                        />

                        <StyledTextField
                            fullWidth
                            required
                            variant="outlined"
                            label="Adresse e-mail"
                            type="email"
                            error={validationErrors.email !== ''}
                            helperText={validationErrors.email}
                            value={props.openedUser.email}
                            onChange={(e) => props.setOpenedUser({ ...props.openedUser, email: e.target.value })}
                            disabled={isLoading}
                        />

                        <ButtonContainer>
                            <CancelButton onClick={onClose} disabled={isLoading}>
                                Annuler
                            </CancelButton>
                            <SubmitButton
                                disabled={Object.values(validationErrors).some(it => it !== "") || isLoading}
                                onClick={updateUser}
                                startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <Edit />}
                            >
                                {isLoading ? "Mise à jour..." : "Mettre à jour"}
                            </SubmitButton>
                        </ButtonContainer>
                    </form>
                </ModalBody>
            </ModalContent>
        </StyledModal>
    );
}
